// Regionsidor för lokal SEO. Varje region samlar ortssidor i ett nav och
// svarar på region-specifika frågor (täckning, restid, pris).
// Primär URL: /filmproduktion-<slug>. Ingen canonical-flytt — regionerna
// har egen sökintention ("filmproduktion skåne") och krockar inte med
// ortssidorna.

export type RegionMeta = {
  slug: string;
  name: string;
  /** H1-vinkel efter ortnamnet. */
  headline: string;
  /** Kort svar högst upp (QuickAnswer / AEO). */
  quickAnswer: string;
  /** Län/områden som ingår — används i schema areaServed och FAQ. */
  areas: string[];
  /** Varifrån regionen bemannas. */
  servedFrom: string;
  /** Ledtid för att vara på plats. */
  leadTime: string;
  /** Ortssidor i regionen. */
  cities: { label: string; path: string }[];
};

export const REGIONS: RegionMeta[] = [
  {
    slug: "skane",
    name: "Skåne",
    headline: "Malmö, Lund, Helsingborg och hela Öresundsregionen",
    quickAnswer:
      "Fenny producerar film i hela Skåne från vårt kontor i Malmö. Vi producerar i Malmö, Lund, Helsingborg, Hyllie, Kristianstad och Öresundsregionen — fast pris, fast leveranstid och inga reseavgifter inom länet.",
    areas: ["Skåne län", "Malmö", "Lund", "Helsingborg", "Kristianstad", "Öresundsregionen"],
    servedFrom: "Malmö",
    leadTime: "Inom 5 arbetsdagar i hela Skåne.",
    cities: [
      { label: "Malmö", path: "/filmproduktion/malmo" },
      { label: "Lund", path: "/filmproduktion/lund" },
      { label: "Helsingborg", path: "/filmproduktion/helsingborg" },
      { label: "Hyllie", path: "/filmproduktion/hyllie" },
      { label: "Kristianstad", path: "/filmproduktion/kristianstad" },
      { label: "Filmbyrå Malmö", path: "/filmbyra/malmo" },
    ],
  },
  {
    slug: "vastsverige",
    name: "Västsverige",
    headline: "Göteborg, Bohuslän, Sjuhärad och Skaraborg",
    quickAnswer:
      "Fenny producerar film i hela Västsverige med crew i Göteborg och produktionsledning från Stockholm. Vi producerar i Storgöteborg, Bohuslän, Sjuhärad, Skaraborg och Halland — samma prislogik som i Stockholm, utan ortspåslag.",
    areas: [
      "Västra Götalands län",
      "Hallands län",
      "Göteborg",
      "Bohuslän",
      "Sjuhärad",
      "Skaraborg",
    ],
    servedFrom: "Göteborg",
    leadTime: "Storgöteborg inom 5 arbetsdagar, övriga Västsverige 5–10.",
    cities: [
      { label: "Göteborg", path: "/filmproduktion/goteborg" },
      { label: "Mölndal", path: "/filmproduktion/molndal" },
      { label: "Hisingen", path: "/filmproduktion/hisingen" },
      { label: "Lindholmen", path: "/filmproduktion/lindholmen" },
      { label: "Borås", path: "/filmproduktion/boras" },
      { label: "Trollhättan", path: "/filmproduktion/trollhattan" },
      { label: "Uddevalla", path: "/filmproduktion/uddevalla" },
      { label: "Skövde", path: "/filmproduktion/skovde" },
      { label: "Varberg", path: "/filmproduktion/varberg" },
    ],
  },
  {
    slug: "norrland",
    name: "Norrland",
    headline: "Umeå, Skellefteå, Luleå, Sundsvall och Östersund",
    quickAnswer:
      "Fenny producerar film i hela Norrland med resande team från Stockholm och lokala samarbetspartners. Vi producerar i Umeå, Skellefteå, Luleå, Sundsvall och Östersund — vanligen med flera inspelningsplatser under samma resa så ni inte betalar för dödtid.",
    areas: [
      "Västerbottens län",
      "Norrbottens län",
      "Västernorrlands län",
      "Jämtlands län",
      "Gävleborgs län",
    ],
    servedFrom: "Stockholm",
    leadTime: "7–14 arbetsdagar, ofta med flera orter samlade i samma resa.",
    cities: [
      { label: "Umeå", path: "/filmproduktion/umea" },
      { label: "Skellefteå", path: "/filmproduktion/skelleftea" },
      { label: "Luleå", path: "/filmproduktion/lulea" },
      { label: "Sundsvall", path: "/filmproduktion/sundsvall" },
      { label: "Östersund", path: "/filmproduktion/ostersund" },
      { label: "Gävle", path: "/filmproduktion/gavle" },
    ],
  },
  {
    slug: "smaland",
    name: "Småland",
    headline: "Jönköping, Växjö, Kalmar och Karlskrona",
    quickAnswer:
      "Fenny producerar film i Småland och Blekinge från våra kontor i Malmö och Stockholm. Vi producerar i Jönköping, Växjö, Kalmar och Karlskrona — med särskild vana av tillverkande industri och familjeägda varumärken.",
    areas: ["Jönköpings län", "Kronobergs län", "Kalmar län", "Blekinge län"],
    servedFrom: "Malmö och Stockholm",
    leadTime: "5–10 arbetsdagar.",
    cities: [
      { label: "Jönköping", path: "/filmproduktion/jonkoping" },
      { label: "Växjö", path: "/filmproduktion/vaxjo" },
      { label: "Kalmar", path: "/filmproduktion/kalmar" },
      { label: "Karlskrona", path: "/filmproduktion/karlskrona" },
      { label: "Filmbyrå Jönköping", path: "/filmbyra/jonkoping" },
    ],
  },
  {
    slug: "ostergotland",
    name: "Östergötland",
    headline: "Linköping, Norrköping och Nyköping",
    quickAnswer:
      "Fenny producerar film i Östergötland och Sörmland från vårt kontor i Stockholm. Vi producerar i Linköping, Norrköping och Nyköping — med vana av deeptech, försvarsindustri och tillverkning.",
    areas: ["Östergötlands län", "Södermanlands län"],
    servedFrom: "Stockholm",
    leadTime: "Inom 5 arbetsdagar.",
    cities: [
      { label: "Linköping", path: "/filmproduktion/linkoping" },
      { label: "Norrköping", path: "/filmproduktion/norrkoping" },
      { label: "Nyköping", path: "/filmproduktion/nykoping" },
      { label: "Filmbyrå Linköping", path: "/filmbyra/linkoping" },
    ],
  },
  {
    slug: "dalarna",
    name: "Dalarna och Värmland",
    headline: "Falun, Borlänge och Karlstad",
    quickAnswer:
      "Fenny producerar film i Dalarna och Värmland från vårt kontor i Västerås. Vi producerar i Falun, Borlänge och Karlstad — med vana av stål, energi och besöksnäring, och locations som fungerar året runt.",
    areas: ["Dalarnas län", "Värmlands län"],
    servedFrom: "Västerås",
    leadTime: "5–10 arbetsdagar.",
    cities: [
      { label: "Falun", path: "/filmproduktion/falun" },
      { label: "Borlänge", path: "/filmproduktion/borlange" },
      { label: "Karlstad", path: "/filmproduktion/karlstad" },
      { label: "Filmbyrå Karlstad", path: "/filmbyra/karlstad" },
    ],
  },
];

export function getRegion(slug: string): RegionMeta | undefined {
  return REGIONS.find((r) => r.slug === slug);
}

export const REGION_SLUGS = REGIONS.map((r) => r.slug);
